/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {currentTimeMillies, Minute} from "@intuitionrobotics/ts-common";
import type {DB_PairedUnit, UnitId} from "./units.js";
import type {UnitConfigurationData} from "./unit-config.js";
import type {ApiTypeBinder} from "@intuitionrobotics/thunderstorm/shared/types";

export enum DemoModeInfo {

}

export type MemoryInfo = {
    maxMemory: number
    usedMemory: number
}

export type BatteryInfo = {
    charging_state: string,
    health: string,
    percentage: number
}

export type HandshakeInfo = {
    state_bt?: string,
    state_wifi?: string,
}

export type KasperInfo = {
    identity: string,
    status: string,
    version: string,
}

export type TimeInfo = {
    device: string
    israel: string
    millis: number
    timezone: string
    utc: string
}

export type MacAddresses = {
    mac_bt: string,
    mac_wifi: string
}

export type WiFiInfo = {
    "Connection Status": string,
    "Signal Strength": string
}

export type WiFiFullInfo = {
    BSSID: string
    "Connection Status": string
    "MAC Address": string
    SSID: string
    "Signal Strength": string
    frequency: string
    hidden: string
    "link speed": string
    rssi: string
    rxSuccessRate: string
    state: string
    txBadRate: string
    txRetriesRate: string
    txSuccessRate: string
    "wifi ip": string
}

export type BluetoothInfo = {
    "Adapter State": string,
    "Advertise State": string,
    "Inquiry State": string,
    "MAC Address": string,
}

export type HwStatusInfo = {
    dsp_status: {
        general_status: string,
        spi_status: string
    },
    mcus_connectivity: {
        head: string,
        leds_mcu: string,
        motors_mcu: string,
        neck: string,
        pan: string,
        robot_mcp: string, // for 3.0 / SO
        robot_usb_hub: string, // for 3.0 / SO
        host_mcu: string, // for 3.0 / SO
    },
    motor_temperatures: {
        head: string,
        neck: string,
        pan: string,
        temperature_zone: string,
    }
    som_resets: number
}

export type VersionName = string;
export type VersionCode = number
export type SystemMode = string;
export type AndroidVersion = number

export type CriticalDeviceInfo = {
    battery?: BatteryInfo
    wifi?: WiFiInfo
    timestamp?: TimeInfo
}

export type PlanInfo = {
    planName: string
    planStatus: string
    timestamp: string
    timestampAsString: string
}
export type Plans = {
    history: PlanInfo[],
    status: { [key: string]: PlanInfo }
}

export type QPulsePairingStatus = {
    is_bluetooth_bonding_present: boolean
    is_paired: boolean
    valid?: boolean
}

export type DeviceTimestampInfo = {
    timestamp?: { millis?: number }
}

export type DeviceInfo = {
    server_connection: string
    battery?: BatteryInfo
    memory_usage: MemoryInfo
    kasper: KasperInfo
    handshake_state?: HandshakeInfo
    demo_mode: DemoModeInfo
    last_boot: TimeInfo
    last_restart: TimeInfo
    last_polling_time: {[appName: string]: number}
    version_name: VersionName
    version_content: string
    version_code: VersionCode
    timestamp?: TimeInfo
    mac_addresses: MacAddresses,
    last_speed_test: string,
    manualShutdown?: boolean
    wifi?: WiFiFullInfo
    system_mode: SystemMode,
    system_state: SystemMode,
    android_version: AndroidVersion,
    speechServer_connection_status: string,
    bluetooth: BluetoothInfo,
    cradle_state?: boolean,
    environment: string,
    hw_status?: HwStatusInfo,
    hw_versions: { [key: string]: string }
    listening_status?: string
    number_of_active_threads: number
    status: string
    fullWifi: WiFiFullInfo
    plans?: Plans
    screen_brightness?: number
    camera_calibration?: string
    qpulse_pairing_status?: QPulsePairingStatus

    error_codes: string
    error_codes_previous: string
    error_code_latest_errors_timestamp: string
    error_code_latest_clear_timestamp: string
    flash_partitions_info: string
    unit_config: UnitConfigurationData
    handshake_type?: string
    strickland_state?: string
    tabletMode?: boolean

    voice_authenticator_enabled?: boolean
    voice_authenticator_enrolled?: boolean
    voice_authenticator_enrolled_type?: string
    voice_authenticator_automatic_enrolling_attempts_reached?: boolean
    voice_authenticator_enrolled_date?: string
    personalized_wakeword_enrolled?: boolean
    personalized_wakeword_enrolled_date?: string

    // How ElliQ speaks to the user (ja-JP only)
    speech_tone?: string

    locale?: string
    ttsProfileId?: string
}

export type TabletInfo = DeviceInfo & {}
export type SomInfo = DeviceInfo & { cradle_state?: boolean, text_service_enabled?: boolean }

export type BaseRuntimeStatus = {
    som?: SomInfo
    tablet?: TabletInfo
}
export type RuntimeStatus = BaseRuntimeStatus & {
    elliqId: string
    comment?: string
}

export type Response_ListUnits = {
    units: { [unitId: string]: RuntimeStatus }
    // pairs: { [unitId: string]: DB_PairedUnit }
}

export type Response_ListPairedUnits = {
    pairs: { [unitId: string]: DB_PairedUnit }
}

export type Request_ListUnits = {
    elliqId: UnitId
}

export const DefaultAliveBuffer = 5 * Minute;
export const DefaultAliveBufferForLastPollingTime = 5 * Minute;

export const isAlive = (timestamp?: number | null, aliveTime: number = DefaultAliveBuffer) => {
    if (!timestamp)
        return false;

    return currentTimeMillies() - timestamp <= aliveTime
};

export type Api_RS_Query = ApiTypeBinder<"/v1/rs/query", { unitIds: string[] }, void, { key: string, value: string }>;